import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = ['SettingsReminders']
const EMPTY_ARRAY = []

export const useSettingsReminders = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/reminder_templates`,
        {
          method: 'GET',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the reminder templates")
      }
      return res.json()
    },
  })

  return { reminders: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useAddReminder = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: addReminder, ...rest } = useMutation({
    mutationFn: async reminderData => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/reminder_templates`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ reminder_template: reminderData }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not create reminder template')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { addReminder, ...rest }
}

export const useUpdateReminder = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateReminder, ...rest } = useMutation({
    mutationFn: async ({ id, ...reminderData }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/reminder_templates/${id}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ reminder_template: reminderData }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update reminder template')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { updateReminder, ...rest }
}

export const useDeleteReminder = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: deleteReminder, ...rest } = useMutation({
    mutationFn: async id => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/reminder_templates/${id}`,
        {
          method: 'DELETE',
          headers,
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not delete reminder template')
      }
      return res.status === 204 ? {} : res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { deleteReminder, ...rest }
}
